import * as React from 'react';
import {
  Breadcrumb as ShadcnBreadcrumb,
  BreadcrumbEllipsis,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from '@/hen-ui/shadcn/breadcrumb';
import { cn } from '@/hen-ui/utils';
import { Link, Text, type LinkProps } from '@/hen-ui/atomics';

const whiteLinkClassName =
  'text-white hover:text-white focus:text-white focus-visible:text-white';

type BreadcrumbSize = 'b1' | 'b2' | 'b3' | 'b4' | 'b5';

const separatorSizeClassName: Record<BreadcrumbSize, string> = {
  b1: 'text-b1',
  b2: 'text-b2',
  b3: 'text-b3',
  b4: 'text-b4',
  b5: 'text-b5',
};

export interface BreadcrumbProps
  extends React.ComponentPropsWithoutRef<typeof ShadcnBreadcrumb> {
  items: {
    label: string;
    href?: string;
  }[];
  maxItems?: number;
  color?: 'default' | 'white';
  linkWeight?: LinkProps['weight'];
  linkUnderline?: LinkProps['underline'];
  /**
   * Body text size for every step and text separator. Unset, steps use `b5`
   * and text separators keep the list text size.
   */
  size?: BreadcrumbSize;
}

function BreadcrumbItemContent({
  label,
  href,
  linkWeight,
  linkUnderline,
  color,
  size,
}: {
  label: string;
  href?: string;
  linkWeight?: LinkProps['weight'];
  linkUnderline?: LinkProps['underline'];
  color: 'default' | 'white';
  size?: BreadcrumbSize;
}) {
  const linkColorClassName = color === 'white' ? whiteLinkClassName : undefined;
  if (href) {
    return (
      <BreadcrumbLink asChild className={linkColorClassName}>
        <Link
          href={href}
          weight={linkWeight}
          underline={linkUnderline}
          className={linkColorClassName}
        >
          <Text
            variant={size ?? 'b5'}
            weight={linkWeight ?? undefined}
            className='text-inherit'
          >
            {label}
          </Text>
        </Link>
      </BreadcrumbLink>
    );
  }
  return (
    <BreadcrumbPage>
      <Text variant={size ?? 'b5'} className='text-inherit'>
        {label}
      </Text>
    </BreadcrumbPage>
  );
}

export const Breadcrumb = React.forwardRef<HTMLElement, BreadcrumbProps>(
  (
    {
      items,
      maxItems = 3,
      color = 'default',
      separator,
      linkWeight,
      linkUnderline,
      size,
      className,
      ...props
    },
    ref,
  ) => {
    if (maxItems < 1) {
      throw new Error('Breadcrumb: maxItems must be at least 1');
    }

    const visibleItems = items.filter((item) => item.label.trim().length > 0);
    if (visibleItems.length === 0) {
      return null;
    }

    const numOfItemsToHide = Math.max(0, visibleItems.length - maxItems);

    const firstItem = visibleItems[0];
    const lastItem = visibleItems[visibleItems.length - 1];
    const hasMultipleItems = visibleItems.length > 1;
    const separatorContent =
      typeof separator === 'string' && separator.trim() === ''
        ? undefined
        : separator;
    const renderSeparator = () => (
      <BreadcrumbSeparator
        className={size ? separatorSizeClassName[size] : undefined}
      >
        {separatorContent}
      </BreadcrumbSeparator>
    );

    return (
      <ShadcnBreadcrumb
        className={cn(
          color === 'white' && '[&_*]:text-white [&_svg]:stroke-white',
          className,
        )}
        ref={ref}
        {...props}
      >
        <BreadcrumbList className='!gap-xxs'>
          {hasMultipleItems && maxItems > 1 && (
            <>
              <BreadcrumbItem>
                <BreadcrumbItemContent
                  label={firstItem.label}
                  href={firstItem.href}
                  linkWeight={linkWeight}
                  linkUnderline={linkUnderline}
                  color={color}
                  size={size}
                />
              </BreadcrumbItem>
              {renderSeparator()}
              {numOfItemsToHide > 0 && (
                <>
                  <BreadcrumbItem>
                    <BreadcrumbEllipsis />
                  </BreadcrumbItem>
                  {renderSeparator()}
                </>
              )}
            </>
          )}
          {visibleItems
            .slice(1 + numOfItemsToHide, visibleItems.length - 1)
            .map(({ label, href }, index) => (
              <React.Fragment key={index}>
                <BreadcrumbItem>
                  <BreadcrumbItemContent
                    label={label}
                    href={href}
                    linkWeight={linkWeight}
                    linkUnderline={linkUnderline}
                    color={color}
                    size={size}
                  />
                </BreadcrumbItem>
                {renderSeparator()}
              </React.Fragment>
            ))}
          <BreadcrumbItem>
            <BreadcrumbItemContent
              label={lastItem.label}
              href={lastItem.href}
              linkWeight={linkWeight}
              linkUnderline={linkUnderline}
              color={color}
              size={size}
            />
          </BreadcrumbItem>
        </BreadcrumbList>
      </ShadcnBreadcrumb>
    );
  },
);
